import { z } from 'zod';
import { syncConsentGateSchema } from './sync-consent.schemas';
import { TRIP_ACCEPTANCES } from './trip-acceptance.schemas';
import { syncHandoverViewSchema } from './trip-handover.schemas';
import { TRIP_LEG_TYPES, TRIP_STATUSES } from './trip.schemas';
import { syncCheckTemplateSchema } from './vehicle-check.schemas';

/**
 * The job card (gitaumoses4/chukua#70): who the load is for and what the driver was given for it. Amounts are what the driver was handed, never
 * what the customer pays, and nothing here names another driver. There is no instructions field yet: the job has none.
 */
export const syncTripCardSchema = z.object({
  customerName: z.string(),
  jobTypeName: z.string(),
  /** The customer's delivery deadline, a calendar date (`YYYY-MM-DD`). */
  deliveryDeadline: z.string().nullable(),
  /** The loose cargo's handling notes, when the job has any. */
  handlingNotes: z.string().nullable(),
  /** The period dispatch expected the trip to take. */
  expectedStart: z.iso.datetime({ offset: true }).nullable(),
  expectedEnd: z.iso.datetime({ offset: true }).nullable(),
  fuelLitres: z.number().int().nullable(),
  expectedTolls: z.object({ amountMinor: z.number().int(), currency: z.string() }).nullable(),
  /** Cash advanced to the driver for this trip, per currency, from the driver ledger. Empty when none. */
  advances: z.array(z.object({ amountMinor: z.number().int(), currency: z.string() })),
});
export type SyncTripCard = z.infer<typeof syncTripCardSchema>;

/** One trip as the driver's app sees it: what to drive, not what it costs or who else is on the job. */
export const syncTripViewSchema = z.object({
  id: z.uuid(),
  sequenceNumber: z.number().int(),
  status: z.enum(TRIP_STATUSES),
  legType: z.enum(TRIP_LEG_TYPES),
  originZoneCode: z.string().nullable(),
  destinationZoneCode: z.string().nullable(),
  jobId: z.uuid(),
  /** A container (by its number) or a loose quantity, one entry per unit the trip carries. */
  cargo: z.array(z.object({ containerNumber: z.string().nullable(), quantity: z.number().int().nullable() })),
  /** Present when the trip was handed to this driver mid-trip (gitaumoses4/chukua#80); additive, so an older app ignores it. */
  handover: syncHandoverViewSchema.optional(),
  /** Additive (gitaumoses4/chukua#70). Only the current trip carries them; `acceptance` is absent until the trip is dispatched. */
  acceptance: z.enum(TRIP_ACCEPTANCES).optional(),
  card: syncTripCardSchema.optional(),
  /** Additive (gitaumoses4/chukua#71). The current trip's pre-trip check template for its truck's class; absent when the class has none, and then the check is not offered. */
  preTripCheck: syncCheckTemplateSchema.optional(),
});
export type SyncTripView = z.infer<typeof syncTripViewSchema>;

/** `GET /sync/trip`: the trip being driven now (null when none) and the last few before it, newest first. `consent: required` means no trip is served until the driver consents to location. */
export const syncTripResponseSchema = z.object({
  current: syncTripViewSchema.nullable(),
  recent: z.array(syncTripViewSchema),
  consent: syncConsentGateSchema,
  /** Additive (gitaumoses4/chukua#496). A company setting, so it is served whatever `consent` is; absent from a server that predates it. `number` null: no fallback. */
  incidentSms: z.object({ number: z.string().nullable(), waitMinutes: z.number().int().min(1) }).optional(),
});
export type SyncTripResponse = z.infer<typeof syncTripResponseSchema>;

/**
 * `GET /sync/trips/history` (gitaumoses4/chukua#347): the driver's last finished trips, newest first, and when the server read them. Trips carry no
 * `card` or `acceptance`: a finished trip waits on nobody. The phone keeps the list whole as a read-only snapshot and shows `syncedAt` beside it.
 */
export const syncTripHistoryResponseSchema = z.object({
  trips: z.array(syncTripViewSchema),
  syncedAt: z.iso.datetime({ offset: true }),
});
export type SyncTripHistoryResponse = z.infer<typeof syncTripHistoryResponseSchema>;
